import { useEffect, useRef, useState } from 'react';

import Arrow from 'assets/images/icons/Arrow.png';
import CollapsedStripBody from 'components/common/chat-bubble/chat-bubble-strip/collapsed-strip-body';
import ExpandStripBody from 'components/common/chat-bubble/chat-bubble-strip/expand-strip-body';
import { WidgetCollapedType } from 'const/widget-option-type';
import { useStoreDetails } from 'context/store-context';
import { isHttpsPresent } from 'helper/is-https-present';
import { Action } from 'hooks/models';
import useActivityLogger from 'hooks/useActivityLogger';
import type { OtherWidget } from 'types';
import { OtherWidgetType } from 'types';

import classes from './chat-bubble-strip.module.scss';

interface IChatBubbleProps {
  chatEnabled: boolean;
  isAgentsOnline: boolean;
  onBubbleButtonClick: () => void;
  onAppointmentClick: () => void;
}

const ChatBubbleStrip = ({
  chatEnabled,
  isAgentsOnline,
  onBubbleButtonClick,
  onAppointmentClick
}: IChatBubbleProps) => {
  const { widgetOption, onlineMenu, offlineMenu, theme, schedulingOption, otherWidgets, callQueuing, conciergeUrl } =
    useStoreDetails();
  const isChatEnabled = chatEnabled || (callQueuing && isAgentsOnline);
  const { log } = useActivityLogger();
  const [isCollapsed, setIsCollapsed] = useState<string>();
  const [onlineButton, setOnlineButton] = useState<boolean>(true);
  const [offlineButton, setOfflineButton] = useState<boolean>(true);
  const otherWidgetsRef = useRef<Map<string, boolean>>(new Map());

  const onClickToggle = (toggleOption?: WidgetCollapedType) => {
    if (toggleOption === isCollapsed) {
      setIsCollapsed(undefined);
    } else {
      setIsCollapsed(toggleOption);
    }
  };

  const hideOtherWidget = (id: string) => {
    const otherWidget = document.querySelector(`#${id}, .${id}`);
    if (otherWidget && otherWidget instanceof HTMLElement) {
      otherWidgetsRef.current.set(id, false);
      otherWidget.style.display = 'none';
      otherWidget.style.visibility = 'hidden';
    }
  };

  useEffect(() => {
    const interval = setInterval(() => {
      otherWidgets.forEach(({ id, type, function: otherFunction }) => {
        if (type === OtherWidgetType.FUNCTION && otherFunction) {
          try {
            const otherWidgetFunction = new Function(otherFunction);
            otherWidgetFunction();
            otherWidgetsRef.current.set(otherFunction, false);
          } catch (error) {
            console.error('other widget function: ', error);
          }
          return;
        }
        if (!id) return;
        hideOtherWidget(id);
      });
      if (otherWidgetsRef.current.size === otherWidgets.length) {
        clearInterval(interval);
      }
    }, 1000);
  }, [otherWidgets]);

  useEffect(() => {
    const handleScroll = () => {
      if (!isCollapsed) {
        setIsCollapsed(WidgetCollapedType.COLLAPSED);
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [isCollapsed]);

  const onPressOfflineLink = () => {
    log({
      action: Action.OFFLINE_LINK_CLICKED_BY_CUSTOMER,
      description: 'customer click offline menu',
      label: 'CUSTOMER_WIDGET'
    });
  };

  const onClickAvatar = () => {
    if (isChatEnabled) {
      onBubbleButtonClick();
    } else {
      if (conciergeUrl) {
        window.location.href = isHttpsPresent(conciergeUrl) ? conciergeUrl : 'https://' + conciergeUrl;
      }
    }
  };

  // This function suppress other widgets and it works as toggle
  const toggleOtherWidget = (otherWidget: OtherWidget) => {
    const { id, function: otherFunction } = otherWidget;
    if (otherWidget.type === OtherWidgetType.FUNCTION && otherFunction) {
      try {
        const otherWidgetFunction = new Function(otherFunction);
        otherWidgetFunction();
        otherWidgetsRef.current.set(otherFunction, false);
      } catch (error) {
        console.error('other widget function: ', error);
      }
      return;
    }
    if (!id) return;
    const currentElement = document.querySelector(`#${id}, .${id}`);

    if (currentElement && currentElement instanceof HTMLElement) {
      const shouldSuppress = currentElement.style.display !== 'none';
      otherWidgetsRef.current.set(id, shouldSuppress);
      currentElement.style.display = shouldSuppress ? 'none' : 'revert';
      currentElement.style.visibility = shouldSuppress ? 'hidden' : 'revert';
    }
  };

  const mobileToggleTwoStyle =
    isCollapsed === WidgetCollapedType.SEMI_COLLAPSED
      ? {
          alignItems: 'center',
          borderRadius: '0px 30px 0px 0px',
          display: 'flex',
          height: '65px',
          justifyContent: 'center',
          paddingBlock: '10px',
          width: '40px'
        }
      : {};

  return (
    <div className={classes.root}>
      <div
        className={classes.cwWidget}
        style={{
          height: isCollapsed ? '30px' : '120px',
          transition: 'height 0.1s',
          width: isCollapsed ? '100%' : '',
          ...mobileToggleTwoStyle
        }}
      >
        {!isCollapsed && (
          <span
            onClick={() => onClickToggle(WidgetCollapedType.COLLAPSED)}
            className={classes.widgetCurveBtn}
          >
            <img
              className={classes.down}
              src={Arrow}
              alt="arrow icon"
            />
          </span>
        )}

        {!isCollapsed ? (
          <ExpandStripBody
            isChatEnabled={isChatEnabled}
            onlineMenu={onlineMenu}
            offlineMenu={offlineMenu}
            onlineButton={onlineButton}
            offlineButton={offlineButton}
            otherWidgets={otherWidgets}
            otherWidgetsRef={otherWidgetsRef}
            theme={theme}
            widgetOption={widgetOption}
            schedulingOption={schedulingOption}
            onBubbleButtonClick={onBubbleButtonClick}
            onAppointmentClick={onAppointmentClick}
            onClickAvatar={onClickAvatar}
            onPressOfflineLink={onPressOfflineLink}
            setOnlineButton={setOnlineButton}
            setOfflineButton={setOfflineButton}
            toggleOtherWidget={toggleOtherWidget}
          />
        ) : (
          <CollapsedStripBody
            isChatEnabled={isChatEnabled}
            isCollapsed={isCollapsed}
            onlineMenu={onlineMenu}
            offlineMenu={offlineMenu}
            onlineButton={onlineButton}
            offlineButton={offlineButton}
            otherWidgets={otherWidgets}
            otherWidgetsRef={otherWidgetsRef}
            theme={theme}
            widgetOption={widgetOption}
            schedulingOption={schedulingOption}
            onBubbleButtonClick={onBubbleButtonClick}
            onAppointmentClick={onAppointmentClick}
            onClickAvatar={onClickAvatar}
            onPressOfflineLink={onPressOfflineLink}
            onClickToggle={onClickToggle}
            setOnlineButton={setOnlineButton}
            setOfflineButton={setOfflineButton}
            toggleOtherWidget={toggleOtherWidget}
          />
        )}
      </div>
    </div>
  );
};

export default ChatBubbleStrip;
